import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse, ApiListResponse } from '../lib/response';
import {
  Settings,
  EmailSending,
  AutoTopUp,
  SMSCountry,
  Reminders,
  Privacy,
  AuthorisedSession,
} from '../models/settings';
import { Minisite } from '../models/minisite';
import { Location } from '../models/location';

export class SettingsService extends BaseService<Settings> {
  override model = Settings;

  override modelName = 'settings';
  override modelLabel = 'Settings';

  async location(): Promise<ApiResponse<Location>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/location`,
    });

    return await this.requestItem(request, Location);
  }

  async privacy(): Promise<ApiResponse<Privacy>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/privacy`,
    });

    return await this.requestItem<Privacy>(request, Privacy);
  }

  async savePrivacy(data: Privacy): Promise<ApiResponse<Privacy>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/privacy`,
      data: data.toJSON ? data.toJSON() : data,
    });

    return await this.requestItem<Privacy>(request, Privacy);
  }

  async minisite(): Promise<ApiResponse<Minisite>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/minisite`,
    });

    return await this.requestItem(request, Minisite);
  }

  async setDomain({
    domain = '',
    confirm = false,
  }: {
    domain?: string;
    confirm?: boolean;
  } = {}): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/setDomain`,
      data: { domain, confirm },
    });

    return await this.request(request);
  }

  async minisiteMenu(): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/minisiteMenu`,
    });

    return await this.request(request);
  }

  async saveMinisiteMenu(
    menu: any,
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/minisiteMenu`,
      data: menu.toJSON ? menu.toJSON() : menu,
    });

    return await this.request(request);
  }

  async emailSending(): Promise<ApiResponse<EmailSending>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/emailSending`,
    });

    return await this.requestItem<EmailSending>(request, EmailSending);
  }

  async saveEmailSending(data: EmailSending): Promise<ApiResponse<EmailSending>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/emailSending`,
      data: data.toJSON ? data.toJSON() : data,
    });

    return await this.requestItem<EmailSending>(request, EmailSending);
  }

  async autoTopupSettings(): Promise<ApiResponse<AutoTopUp>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/autoTopupSettings`,
    });

    return await this.requestItem<AutoTopUp>(request, AutoTopUp);
  }

  async saveAutoTopupSettings(data: AutoTopUp): Promise<ApiResponse<AutoTopUp>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/autoTopupSettings`,
      data: data.toJSON(),
    });

    return await this.requestItem<AutoTopUp>(request, AutoTopUp);
  }

  async smsCountry(): Promise<ApiResponse<SMSCountry>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/smsCountry`,
    });

    return await this.requestItem<SMSCountry>(request, SMSCountry);
  }

  async saveSmsCountry(data: SMSCountry): Promise<ApiResponse<SMSCountry>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/smsCountry`,
      data: data.toJSON ? data.toJSON() : data,
    });

    return await this.requestItem<SMSCountry>(request, SMSCountry);
  }

  async reminders(): Promise<ApiResponse<Reminders>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/reminders`,
    });

    return await this.requestItem<Reminders>(request, Reminders);
  }

  async saveReminders(data: Reminders): Promise<ApiResponse<Reminders>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/reminders`,
      data: data.toJSON ? data.toJSON() : data,
    });

    return await this.requestItem<Reminders>(request, Reminders);
  }

  async sessions(page: number = 1): Promise<ApiListResponse<AuthorisedSession>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/authorisedSessions`,
      query: { page: page.toString() },
    });

    return await this.requestList<AuthorisedSession>(request, AuthorisedSession);
  }

  async revokeEmployeeSessions(employeeId: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/revokeSession`,
      query: { idEmployee: employeeId },
    });

    return await this.request(request);
  }

  async revokeSession(session: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/revokeSession`,
      query: { session },
    });

    return await this.request(request);
  }

  async policies(): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/viewPolicies`,
    });

    return await this.request(request);
  }

  async acceptPolicy({
    policy,
    termsAgreed,
  }: {
    policy: string;
    termsAgreed: boolean;
  }): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/acceptPolicy`,
      data: { policy, termsAgreed },
    });

    return await this.request(request);
  }

  async setLogoImage(image: File): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('logo', image);

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/logoImage`,
      data: formData,
    });

    return await this.request(request);
  }

  async removeLogoImage(): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'DELETE',
      url: `${this.modelBaseUrl}/logoImage`,
    });

    return await this.request(request);
  }

  async checkCustomDomain(): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/checkCustomDomain`,
    });

    return await this.request(request);
  }

  async setCustomDomain({
    domain,
    enable = true,
  }: {
    domain: string;
    enable?: boolean;
  }): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/customDomain`,
      data: { domain, enable },
    });

    return await this.request(request);
  }
}
